import { useDepartmentText } from 'model/DepartmentTerminology';
import { Menu, TextInput, TextInputProps } from '@mantine/core';
import PlaceholderMenu from 'pages/revisions-module/template-editor/editors/template/extensions/placeholder/PlaceholderMenu';
import { noop } from 'lodash';
import { useMemo } from 'react';
import { useFormInputs } from 'pages/revisions-module/template-editor/editors/form/input/FormInputsDataProvider';
import { SPECIAL_PLACEHOLDERS } from 'pages/revisions-module/template-editor/editors/template/extensions/placeholder/Placeholder';
import { usePipelineData } from 'pages/revisions-module/template-editor/editors/pipeline/PipelineDataProvider';
import { useDepartmentPlaceholders } from 'pages/revisions-module/template-editor/editors/template/extensions/placeholder/department-placeholders';

export interface IPlaceholderSelectProps extends Omit<TextInputProps, 'value' | 'onChange'> {
  value?: string;
  onChange?: (value: string) => void;
  excludeSpecialTechnicianProperties?: boolean;
  excludeSpecialRevisionProperties?: boolean;
  excludeSpecialOrganizationProperties?: boolean;
  excludeSpecialDepartmentProperties?: boolean;
  excludeSpecialDocumentProperties?: boolean;
}

/**
 * Used to select a placeholder from the PlaceholderMenu.
 */
export default function PlaceholderSelect({
  value,
  onChange = noop,
  excludeSpecialTechnicianProperties = false,
  excludeSpecialRevisionProperties = false,
  excludeSpecialOrganizationProperties = false,
  excludeSpecialDepartmentProperties = false,
  excludeSpecialDocumentProperties = false,
  ...textInputProps
}: IPlaceholderSelectProps) {
  const departmentText = useDepartmentText();
  const departmentPlaceholders = useDepartmentPlaceholders();
  const { getInputByName } = useFormInputs();
  const { placeholderGroups: pipelinePlaceholderGroups } = usePipelineData();

  const label = useMemo(() => {
    if (!value) {
      return '';
    }

    for (const { placeholders } of pipelinePlaceholderGroups) {
      const placeholder = placeholders.find((p) => p.name === value);

      if (placeholder) {
        return placeholder.label;
      }
    }

    const input = getInputByName(value);
    const special = [...SPECIAL_PLACEHOLDERS, ...departmentPlaceholders].find(
      (placeholder) => placeholder.name === value
    );

    return (
      input?.spec.label ??
      (special?.name === '_department'
        ? departmentText('Položka úrovne', 'Stredisko')
        : special?.name === '_departmentPath'
        ? departmentText('Celá cesta položky úrovne', 'Celá cesta strediska')
        : special?.label) ??
      ''
    );
  }, [value, getInputByName, pipelinePlaceholderGroups, departmentText, departmentPlaceholders]);

  return (
    <Menu withinPortal>
      <Menu.Target>
        <TextInput value={label} onChange={noop} {...textInputProps} />
      </Menu.Target>
      <Menu.Dropdown>
        <PlaceholderMenu
          onClick={(placeholder) => onChange(placeholder.name)}
          excludeSpecialTechnicianProperties={excludeSpecialTechnicianProperties}
          excludeSpecialRevisionProperties={excludeSpecialRevisionProperties}
          excludeSpecialOrganizationProperties={excludeSpecialOrganizationProperties}
          excludeSpecialDepartmentProperties={excludeSpecialDepartmentProperties}
          excludeSpecialDocumentProperties={excludeSpecialDocumentProperties}
        />
      </Menu.Dropdown>
    </Menu>
  );
}
